import THREE from 'three';

class GizmoMaterial extends THREE.MeshBasicMaterial {
  constructor(parameters) {

    super();

    this.depthTest = false;
    this.depthWrite = false;
    this.side = THREE.FrontSide;
    this.transparent = true;

    this.setValues(parameters);

    this.oldColor = this.color.clone();
    this.oldOpacity = this.opacity;

  }

  highlight(highlighted) {

    if (highlighted) {

      this.color.setRGB(1, 230 / 255, 3 / 255);
      this.opacity = 1;
      this.uniformsNeedUpdate = true; // needed by WebGPU

    } else {

      this.color.copy(this.oldColor);
      this.opacity = this.oldOpacity;
      this.uniformsNeedUpdate = true; // needed by WebGPU
    }

  }
}


class GizmoLineMaterial extends THREE.LineBasicMaterial {

  constructor(parameters) {

    super();

    this.depthTest = false;
    this.depthWrite = false;
    this.transparent = true;
    this.linewidth = 1;

    this.setValues(parameters);

    this.oldColor = this.color.clone();
    this.oldOpacity = this.opacity;

  }

  highlight(highlighted) {

    if (highlighted) {

      this.color.setRGB(1, 230 / 255, 3 / 255);
      this.opacity = 1;

    } else {

      this.color.copy(this.oldColor);
      this.opacity = this.oldOpacity;

    }

  }
}


function createCircleGeometry(radius, facing, arc) {
  const vertices = [];
  arc = arc ? arc : 1;
  for (var i = 0; i <= 64 * arc; ++i) {
    if (facing == 'x') vertices.push(new THREE.Vector3(0, Math.cos(i / 32 * Math.PI), Math.sin(i / 32 * Math.PI)).multiplyScalar(radius));
    if (facing == 'y') vertices.push(new THREE.Vector3(Math.cos(i / 32 * Math.PI), 0, Math.sin(i / 32 * Math.PI)).multiplyScalar(radius));
    if (facing == 'z') vertices.push(new THREE.Vector3(Math.sin(i / 32 * Math.PI), Math.cos(i / 32 * Math.PI), 0).multiplyScalar(radius));
  }

  return new THREE.BufferGeometry().setFromPoints(vertices);
}

function createArrowGeometry(radiusTop, radiusBottom, height, radialSegments, heightSegments, openEnded) {
  const arrowGeometry = new THREE.CylinderBufferGeometry(radiusTop, radiusBottom, height, radialSegments, heightSegments, openEnded);
  return arrowGeometry.applyMatrix4(new THREE.Matrix4().makeTranslation(0, 0.5, 0));
}

function createLineGeometry(axis) {
  const vertices = [];
  if (axis === 'X')
  vertices.push(new THREE.Vector3(0, 0, 0), new THREE.Vector3(1, 0, 0));else
  if (axis === 'Y')
  vertices.push(new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, 1, 0));else
  if (axis === 'Z')
  vertices.push(new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, 0, 1));

  const lineGeometry = new THREE.BufferGeometry().setFromPoints(vertices);
  lineGeometry.isLines = true;

  return lineGeometry;
}


export class TransformGizmo extends THREE.Object3D {

  constructor(includeAxis) {

    super();

    this._includeAxis = includeAxis;
    this._showPickers = false; //debug
    this._showActivePlane = false; //debug

  }

  init() {
    this.handles = new THREE.Object3D();
    this.pickers = new THREE.Object3D();
    this.planes = new THREE.Object3D();
    this.highlights = new THREE.Object3D();
    this.hemiPicker = new THREE.Object3D();
    this.subPickers = new THREE.Object3D();

    this.add(this.handles);
    this.add(this.pickers);
    this.add(this.planes);
    this.add(this.highlights);
    this.add(this.hemiPicker);
    this.add(this.subPickers);

    //// PLANES

    var planeGeometry = new THREE.PlaneBufferGeometry(50, 50, 2, 2);
    var planeMaterial = new THREE.MeshBasicMaterial({ wireframe: true });
    planeMaterial.side = THREE.DoubleSide;

    var planes = {
      "XY": new THREE.Mesh(planeGeometry, planeMaterial),
      "YZ": new THREE.Mesh(planeGeometry, planeMaterial),
      "XZ": new THREE.Mesh(planeGeometry, planeMaterial),
      "XYZE": new THREE.Mesh(planeGeometry, planeMaterial)
    };

    this.activePlane = planes["XYZE"];

    planes["YZ"].rotation.set(0, Math.PI / 2, 0);
    planes["XZ"].rotation.set(-Math.PI / 2, 0, 0);

    for (var i in planes) {
      planes[i].name = i;
      this.planes.add(planes[i]);
      this.planes[i] = planes[i];
      planes[i].visible = false;
    }

    this.setupGizmos();
    this.activeMode = "";

    // reset Transformations

    this.traverse(function (child) {
      if (child instanceof THREE.Mesh) {
        child.updateMatrix();

        const tempGeometry = child.geometry.clone();
        tempGeometry.applyMatrix4(child.matrix);
        child.geometry = tempGeometry;

        child.position.set(0, 0, 0);
        child.rotation.set(0, 0, 0);
        child.scale.set(1, 1, 1);
      }
    });

  }

  hide() {
    this.traverse(function (child) {
      child.visible = false;
    });
  }

  show() {
    this.traverse((child) => {
      child.visible = true;
      if (child.parent == this.pickers || child.parent == this.hemiPicker) child.visible = this._showPickers;
      if (child.parent == this.planes) child.visible = false;
    });
    this.activePlane.visible = this._showActivePlane;
  }

  highlight(axis) {
    this.traverse(function (child) {
      if (child.material && child.material.highlight) {
        if (child.name == axis) {
          child.material.highlight(true);
        } else {
          child.material.highlight(false);
        }
      }
    });
  }

  setupGizmos() {

    var addGizmos = function (gizmoMap, parent) {

      for (var name in gizmoMap) {

        for (var i = gizmoMap[name].length; i--;) {

          var object = gizmoMap[name][i][0];
          var position = gizmoMap[name][i][1];
          var rotation = gizmoMap[name][i][2];
          var visble = gizmoMap[name][i][3];

          object.name = name;

          if (position) object.position.set(position[0], position[1], position[2]);
          if (rotation) object.rotation.set(rotation[0], rotation[1], rotation[2]);
          if (visble) object.visble = visble;

          parent.add(object);

        }

      }

    };

    this.setHandlePickerGizmos();

    if (this._includeAxis) {
      var axisNames = Object.keys(this.handleGizmos);

      for (var i = 0; i < axisNames.length; i++) {
        var axisName = axisNames[i];

        if (this._includeAxis.indexOf(axisName) === -1) {
          delete this.handleGizmos[axisName];
          delete this.pickerGizmos[axisName];
          delete this.hemiPickerGizmos[axisName];
        }
      }
    }

    addGizmos(this.handleGizmos, this.handles);
    addGizmos(this.pickerGizmos, this.pickers);
    addGizmos(this.highlightGizmos, this.highlights);
    addGizmos(this.hemiPickerGizmos, this.hemiPicker);
    addGizmos(this.subPickerGizmos, this.subPickers);

    this.hide();
    this.show();

  }

  update(rotation, eye) {

    var vec1 = new THREE.Vector3(0, 0, 0);
    var vec2 = new THREE.Vector3(0, 1, 0);
    var lookAtMatrix = new THREE.Matrix4();

    this.traverse(function (child) {
      if (child.name) {
        if (child.name.search("E") != -1) {
          child.quaternion.setFromRotationMatrix(lookAtMatrix.lookAt(eye, vec1, vec2));
        } else if (child.name.search("X") != -1 || child.name.search("Y") != -1 || child.name.search("Z") != -1) {
          child.quaternion.setFromEuler(rotation);
        }
      }
    });

  }
}


export class TransformGizmoRotate extends TransformGizmo {

  constructor(includeAxis) {

    super(includeAxis);

    this.init();

  }

  setHandlePickerGizmos() {

    this.handleGizmos = {
      RX: [
      [new THREE.Line(createCircleGeometry(1, 'x', 0.5), new GizmoLineMaterial({ color: 0xff0000 }))]],

      RY: [
      [new THREE.Line(createCircleGeometry(1, 'y', 0.5), new GizmoLineMaterial({ color: 0x00ff00 }))]],

      RZ: [
      [new THREE.Line(createCircleGeometry(1, 'z', 0.5), new GizmoLineMaterial({ color: 0x0000ff }))]],

      RE: [
      [new THREE.Line(createCircleGeometry(1.25, 'z', 1), new GizmoLineMaterial({ color: 0x00ffff }))]],

      RXYZE: [
      [new THREE.Line(createCircleGeometry(1, 'z', 1), new GizmoLineMaterial({ color: 0xff00ff }))]]

    };

    this.pickerGizmos = {
      RX: [
      [new THREE.Mesh(new THREE.TorusBufferGeometry(1, 0.12, 4, 12, Math.PI), new GizmoMaterial({ color: 0xff0000, opacity: 0.25 })), [0, 0, 0], [0, -Math.PI / 2, -Math.PI / 2]]],

      RY: [
      [new THREE.Mesh(new THREE.TorusBufferGeometry(1, 0.12, 4, 12, Math.PI), new GizmoMaterial({ color: 0x00ff00, opacity: 0.25 })), [0, 0, 0], [Math.PI / 2, 0, 0]]],

      RZ: [
      [new THREE.Mesh(new THREE.TorusBufferGeometry(1, 0.12, 4, 12, Math.PI), new GizmoMaterial({ color: 0x0000ff, opacity: 0.25 })), [0, 0, 0], [0, 0, -Math.PI / 2]]],

      RE: [
      [new THREE.Mesh(new THREE.TorusBufferGeometry(1.25, 0.12, 2, 24), new GizmoMaterial({ color: 0x00ffff, opacity: 0.25 }))]],

      RXYZE: [
      [new THREE.Mesh(new THREE.TorusBufferGeometry(1, 0.12, 2, 24), new GizmoMaterial({ color: 0xff00ff, opacity: 0.25 }))]]

    };

  }

  setActivePlane(axis) {

    if (axis == "RE") this.activePlane = this.planes["XYZE"];

    if (axis == "RX") this.activePlane = this.planes["YZ"];

    if (axis == "RY") this.activePlane = this.planes["XZ"];

    if (axis == "RZ") this.activePlane = this.planes["XY"];

    this.hide();
    this.show();

  }

  update(rotation, eye2) {

    super.update(rotation, eye2);

    var tempMatrix = new THREE.Matrix4();
    var worldRotation = new THREE.Euler(0, 0, 1);
    var tempQuaternion = new THREE.Quaternion();
    var unitX = new THREE.Vector3(1, 0, 0);
    var unitY = new THREE.Vector3(0, 1, 0);
    var unitZ = new THREE.Vector3(0, 0, 1);
    var quaternionX = new THREE.Quaternion();
    var quaternionY = new THREE.Quaternion();
    var quaternionZ = new THREE.Quaternion();
    var eye = eye2.clone();

    worldRotation.copy(this.planes["XY"].rotation);
    tempQuaternion.setFromEuler(worldRotation);

    tempMatrix.makeRotationFromQuaternion(tempQuaternion).invert();
    eye.applyMatrix4(tempMatrix);

    this.traverse(function (child) {

      tempQuaternion.setFromEuler(worldRotation);

      if (child.name == "RX") {
        quaternionX.setFromAxisAngle(unitX, Math.atan2(-eye.y, eye.z));
        tempQuaternion.multiplyQuaternions(tempQuaternion, quaternionX);
        child.quaternion.copy(tempQuaternion);
      }

      if (child.name == "RY") {
        quaternionY.setFromAxisAngle(unitY, Math.atan2(eye.x, eye.z));
        tempQuaternion.multiplyQuaternions(tempQuaternion, quaternionY);
        child.quaternion.copy(tempQuaternion);
      }

      if (child.name == "RZ") {
        quaternionZ.setFromAxisAngle(unitZ, Math.atan2(eye.y, eye.x));
        tempQuaternion.multiplyQuaternions(tempQuaternion, quaternionZ);
        child.quaternion.copy(tempQuaternion);
      }

    });

  }
}


export class TransformGizmoScale extends TransformGizmo {

  constructor(includeAxis) {

    super(includeAxis);

    this.init();

  }

  setHandlePickerGizmos() {

    var arrowGeometry = createArrowGeometry(0.125, 0.125, 0.125);
    var lineXGeometry = createLineGeometry('X');
    var lineYGeometry = createLineGeometry('Y');
    var lineZGeometry = createLineGeometry('Z');

    this.handleGizmos = {
      X: [
      [new THREE.Mesh(arrowGeometry, new GizmoMaterial({ color: 0xff0000 })), [0.5, 0, 0], [0, 0, -Math.PI / 2]],
      [new THREE.Line(lineXGeometry, new GizmoLineMaterial({ color: 0xff0000 }))]],

      Y: [
      [new THREE.Mesh(arrowGeometry, new GizmoMaterial({ color: 0x00ff00 })), [0, 0.5, 0]],
      [new THREE.Line(lineYGeometry, new GizmoLineMaterial({ color: 0x00ff00 }))]],

      Z: [
      [new THREE.Mesh(arrowGeometry, new GizmoMaterial({ color: 0x0000ff })), [0, 0, 0.5], [Math.PI / 2, 0, 0]],
      [new THREE.Line(lineZGeometry, new GizmoLineMaterial({ color: 0x0000ff }))]],

      XYZ: [
      [new THREE.Mesh(new THREE.BoxBufferGeometry(0.125, 0.125, 0.125), new GizmoMaterial({ color: 0xffffff, opacity: 0.25 }))]]

    };

    this.pickerGizmos = {
      X: [
      [new THREE.Mesh(new THREE.CylinderBufferGeometry(0.2, 0, 1, 4, 1, false), new GizmoMaterial({ color: 0xff0000, opacity: 0.25 })), [0.6, 0, 0], [0, 0, -Math.PI / 2]]],

      Y: [
      [new THREE.Mesh(new THREE.CylinderBufferGeometry(0.2, 0, 1, 4, 1, false), new GizmoMaterial({ color: 0x00ff00, opacity: 0.25 })), [0, 0.6, 0]]],

      Z: [
      [new THREE.Mesh(new THREE.CylinderBufferGeometry(0.2, 0, 1, 4, 1, false), new GizmoMaterial({ color: 0x0000ff, opacity: 0.25 })), [0, 0, 0.6], [Math.PI / 2, 0, 0]]],

      XYZ: [
      [new THREE.Mesh(new THREE.BoxBufferGeometry(0.4, 0.4, 0.4), new GizmoMaterial({ color: 0xffffff, opacity: 0.25 }))]]

    };

  }

  setActivePlane(axis, eye, isLocalSpace) {

    var tempMatrix = new THREE.Matrix4();
    eye.applyMatrix4(tempMatrix.extractRotation(this.planes["XY"].matrixWorld).invert());

    if (axis == "X") {
      this.activePlane = this.planes["XY"];
      if (!isLocalSpace && Math.abs(eye.y) > Math.abs(eye.z)) this.activePlane = this.planes["XZ"];
    }

    if (axis == "Y") {
      this.activePlane = this.planes["XY"];
      if (!isLocalSpace && Math.abs(eye.x) > Math.abs(eye.z)) this.activePlane = this.planes["YZ"];
    }

    if (axis == "Z") {
      this.activePlane = this.planes["XZ"];
      if (!isLocalSpace && Math.abs(eye.x) > Math.abs(eye.y)) this.activePlane = this.planes["YZ"];
    }

    if (axis == "XYZ") this.activePlane = this.planes["XYZE"];

    this.hide();
    this.show();

  }
}


export class TransformGizmoTranslate extends TransformGizmo {

  constructor(includeAxis) {

    super(includeAxis);

    this.init();

  }

  setHandlePickerGizmos() {

    var arrowGeometry = createArrowGeometry(0, 0.05, 0.2, 12, 1, false);
    var lineXGeometry = createLineGeometry('X');
    var lineYGeometry = createLineGeometry('Y');
    var lineZGeometry = createLineGeometry('Z');

    this.handleGizmos = {
      X: [
      [new THREE.Mesh(arrowGeometry, new GizmoMaterial({ color: 0xf12c2c })), [0.5, 0, 0], [0, 0, -Math.PI / 2]],
      [new THREE.Line(lineXGeometry, new GizmoLineMaterial({ color: 0xf12c2c }))]],

      Y: [
      [new THREE.Mesh(arrowGeometry, new GizmoMaterial({ color: 0x0bb80b })), [0, 0.5, 0]],
      [new THREE.Line(lineYGeometry, new GizmoLineMaterial({ color: 0x0bb80b }))]],

      Z: [
      [new THREE.Mesh(arrowGeometry, new GizmoMaterial({ color: 0x2c2cf1 })), [0, 0, 0.5], [Math.PI / 2, 0, 0]],
      [new THREE.Line(lineZGeometry, new GizmoLineMaterial({ color: 0x2c2cf1 }))]],

      XYZ: [
      [new THREE.Mesh(new THREE.OctahedronBufferGeometry(0.1, 0), new GizmoMaterial({ color: 0xffffff, opacity: 0.25 })), [0, 0, 0], [0, 0, 0]]],

      XY: [
      [new THREE.Mesh(new THREE.PlaneBufferGeometry(0.29, 0.29), new GizmoMaterial({ color: 0xffff00, opacity: 0.25 })), [0.15, 0.15, 0]]],

      YZ: [
      [new THREE.Mesh(new THREE.PlaneBufferGeometry(0.29, 0.29), new GizmoMaterial({ color: 0x00ffff, opacity: 0.25 })), [0, 0.15, 0.15], [0, Math.PI / 2, 0]]],

      XZ: [
      [new THREE.Mesh(new THREE.PlaneBufferGeometry(0.29, 0.29), new GizmoMaterial({ color: 0xff00ff, opacity: 0.25 })), [0.15, 0, 0.15], [-Math.PI / 2, 0, 0]]]

    };

    this.pickerGizmos = {
      X: [
      [new THREE.Mesh(new THREE.CylinderBufferGeometry(0.2, 0, 1, 4, 1, false), new GizmoMaterial({ color: 0xff0000, opacity: 0.25 })), [0.6, 0, 0], [0, 0, -Math.PI / 2]]],

      Y: [
      [new THREE.Mesh(new THREE.CylinderBufferGeometry(0.2, 0, 1, 4, 1, false), new GizmoMaterial({ color: 0x00ff00, opacity: 0.25 })), [0, 0.6, 0]]],

      Z: [
      [new THREE.Mesh(new THREE.CylinderBufferGeometry(0.2, 0, 1, 4, 1, false), new GizmoMaterial({ color: 0x0000ff, opacity: 0.25 })), [0, 0, 0.6], [Math.PI / 2, 0, 0]]],

      XYZ: [
      [new THREE.Mesh(new THREE.OctahedronBufferGeometry(0.2, 0), new GizmoMaterial({ color: 0xffffff, opacity: 0.25 }))]],

      XY: [
      [new THREE.Mesh(new THREE.PlaneBufferGeometry(0.4, 0.4), new GizmoMaterial({ color: 0xffff00, opacity: 0.25 })), [0.2, 0.2, 0]]],

      YZ: [
      [new THREE.Mesh(new THREE.PlaneBufferGeometry(0.4, 0.4), new GizmoMaterial({ color: 0x00ffff, opacity: 0.25 })), [0, 0.2, 0.2], [0, Math.PI / 2, 0]]],

      XZ: [
      [new THREE.Mesh(new THREE.PlaneBufferGeometry(0.4, 0.4), new GizmoMaterial({ color: 0xff00ff, opacity: 0.25 })), [0.2, 0, 0.2], [-Math.PI / 2, 0, 0]]]

    };

    this.hemiPickerGizmos = {
      XYZ: [
      [new THREE.Mesh(new THREE.BoxBufferGeometry(1.2, 1.2, 1.2), new GizmoMaterial({ color: 0x0000ff })), [0.5, 0.5, 0.5], null, false]]

    };

  }

  setActivePlane(axis, eye, isLocalSpace) {

    var tempMatrix = new THREE.Matrix4();
    eye.applyMatrix4(tempMatrix.extractRotation(this.planes["XY"].matrixWorld).invert());

    if (axis == "X") {
      this.activePlane = this.planes["XY"];
      if (!isLocalSpace && Math.abs(eye.y) > Math.abs(eye.z)) this.activePlane = this.planes["XZ"];
    }

    if (axis == "Y") {
      this.activePlane = this.planes["XY"];
      if (!isLocalSpace && Math.abs(eye.x) > Math.abs(eye.z)) this.activePlane = this.planes["YZ"];
    }

    if (axis == "Z") {
      this.activePlane = this.planes["XZ"];
      if (!isLocalSpace && Math.abs(eye.x) > Math.abs(eye.y)) this.activePlane = this.planes["YZ"];
    }

    if (axis == "XYZ") this.activePlane = this.planes["XYZE"];

    if (axis == "XY") this.activePlane = this.planes["XY"];

    if (axis == "YZ") this.activePlane = this.planes["YZ"];

    if (axis == "XZ") this.activePlane = this.planes["XZ"];

    this.hide();
    this.show();

  }
}


export class TransformGizmoTranslateRotate extends TransformGizmo {

  constructor(includeAxis) {

    super(includeAxis);

    this.init();

  }

  setHandlePickerGizmos() {

    var arrowGeometry = createArrowGeometry(0, 0.05, 0.2, 12, 1, false);
    var theta = 0.15;

    this.handleGizmos = {
      Z: [
      [new THREE.Mesh(arrowGeometry, new GizmoMaterial({ color: 0xffffff })), [0, 0, 0.25], [Math.PI / 2, 0, 0]],
      [new THREE.Mesh(new THREE.CylinderBufferGeometry(0.015, 0.015, 0.6, 4, 1, false), new GizmoMaterial({ color: 0xffffff })), [0, 0, 0.5], [Math.PI / 2, 0, 0]]],

      RX: [
      [new THREE.Mesh(new THREE.TorusBufferGeometry(1, 0.015, 12, 60, theta * 2 * Math.PI), new GizmoMaterial({ color: 0xff0000 })), [0, 0, 0], [theta * Math.PI, -Math.PI / 2, 0]],
      [new THREE.Mesh(new THREE.CylinderBufferGeometry(0.05, 0.05, 0.015, 60, 1, false), new GizmoMaterial({ color: 0xff0000 })), [0, 0, 1], [Math.PI / 2, 0, 0]]],

      RY: [
      [new THREE.Mesh(new THREE.TorusBufferGeometry(1, 0.015, 12, 60, theta * 2 * Math.PI), new GizmoMaterial({ color: 0x0000ff })), [0, 0, 0], [Math.PI / 2, 0, (0.5 - theta) * Math.PI]],
      [new THREE.Mesh(new THREE.CylinderBufferGeometry(0.05, 0.05, 0.01, 60, 1, false), new GizmoMaterial({ color: 0x0000ff })), [0, 0, 1]]]

    };

    this.pickerGizmos = {
      Z: [
      [new THREE.Mesh(new THREE.CylinderBufferGeometry(0.12, 0.12, 0.65, 4, 1, false), new GizmoMaterial({ color: 0x0000ff, opacity: 0.25 })), [0, 0, 0.5], [Math.PI / 2, 0, 0]]],

      RX: [
      [new THREE.Mesh(new THREE.TorusBufferGeometry(1, 0.12, 4, 12, theta * 2 * Math.PI), new GizmoMaterial({ color: 0xff0000, opacity: 0.25 })), [0, 0, 0], [theta * Math.PI, -Math.PI / 2, 0]]],

      RY: [
      [new THREE.Mesh(new THREE.TorusBufferGeometry(1, 0.12, 4, 12, theta * 2 * Math.PI), new GizmoMaterial({ color: 0x0000ff, opacity: 0.25 })), [0, 0, 0], [Math.PI / 2, 0, (0.5 - theta) * Math.PI]]]

    };

    this.subPickerGizmos = {
      Z: [
      [new THREE.Mesh(new THREE.CylinderBufferGeometry(0.12, 0.12, 0.65, 4, 1, false), new GizmoMaterial({ color: 0x0000ff, opacity: 0.25 })), [0, 0, 0.5], [Math.PI / 2, 0, 0]]]

    };

    this.highlightGizmos = {
      Z: [],

      RX: [
      [new THREE.Mesh(new THREE.TorusBufferGeometry(1, 0.02, 12, 60, 2 * Math.PI), new GizmoMaterial({ color: 0xff0000, opacity: 1 })), [0, 0, 0], [0, -Math.PI / 2, -Math.PI / 2], false]],

      RY: [
      [new THREE.Mesh(new THREE.TorusBufferGeometry(1, 0.02, 12, 60, 2 * Math.PI), new GizmoMaterial({ color: 0x0000ff, opacity: 1 })), [0, 0, 0], [Math.PI / 2, 0, 0], false]]

    };

    this.hemiPickerGizmos = {
      XYZ: [
      [new THREE.Mesh(new THREE.SphereBufferGeometry(1.2, 8, 8, 0, Math.PI), new GizmoMaterial({ color: 0x0000ff })), null, null, false]]

    };

  }

  setActivePlane(axis, eye, isLocalSpace) {

    if (this.activeMode == "translate") {

      var tempMatrix = new THREE.Matrix4();
      eye.applyMatrix4(tempMatrix.extractRotation(this.planes["XY"].matrixWorld).invert());

      if (axis == "X") {
        this.activePlane = this.planes["XY"];
        if (!isLocalSpace && Math.abs(eye.y) > Math.abs(eye.z)) this.activePlane = this.planes["XZ"];
      }

      if (axis == "Y") {
        this.activePlane = this.planes["XY"];
        if (!isLocalSpace && Math.abs(eye.x) > Math.abs(eye.z)) this.activePlane = this.planes["YZ"];
      }

      if (axis == "Z") {
        this.activePlane = this.planes["XZ"];
        if (!isLocalSpace && Math.abs(eye.x) > Math.abs(eye.y)) this.activePlane = this.planes["YZ"];
      }

    } else if (this.activeMode == "rotate") {

      if (axis == "RX") this.activePlane = this.planes["YZ"];

      if (axis == "RY") this.activePlane = this.planes["XZ"];

      if (axis == "RZ") this.activePlane = this.planes["XY"];

    }

    this.hide();
    this.show();

  }

  update(rotation, eye2) {

    if (this.activeMode == "translate") {

      super.update(rotation, eye2);

    } else if (this.activeMode == "rotate") {

      super.update(rotation, eye2);

      var tempMatrix = new THREE.Matrix4();
      var worldRotation = new THREE.Euler(0, 0, 1);
      var tempQuaternion = new THREE.Quaternion();
      var unitX = new THREE.Vector3(1, 0, 0);
      var unitY = new THREE.Vector3(0, 1, 0);
      var unitZ = new THREE.Vector3(0, 0, 1);
      var quaternionX = new THREE.Quaternion();
      var quaternionY = new THREE.Quaternion();
      var quaternionZ = new THREE.Quaternion();
      var eye = eye2.clone();

      worldRotation.copy(this.planes["XY"].rotation);
      tempQuaternion.setFromEuler(worldRotation);

      tempMatrix.makeRotationFromQuaternion(tempQuaternion).invert();
      eye.applyMatrix4(tempMatrix);

      this.traverse(function (child) {

        tempQuaternion.setFromEuler(worldRotation);

        if (child.name == "RX") {
          quaternionX.setFromAxisAngle(unitX, Math.atan2(-eye.y, eye.z));
          tempQuaternion.multiplyQuaternions(tempQuaternion, quaternionX);
          child.quaternion.copy(tempQuaternion);
        }

        if (child.name == "RY") {
          quaternionY.setFromAxisAngle(unitY, Math.atan2(eye.x, eye.z));
          tempQuaternion.multiplyQuaternions(tempQuaternion, quaternionY);
          child.quaternion.copy(tempQuaternion);
        }

        if (child.name == "RZ") {
          quaternionZ.setFromAxisAngle(unitZ, Math.atan2(eye.y, eye.x));
          tempQuaternion.multiplyQuaternions(tempQuaternion, quaternionZ);
          child.quaternion.copy(tempQuaternion);
        }

      });

    }

  }

  show() {
    this.traverse((child) => {
      if (this.parent == null || this.parent.useAllPickers || child.parent != this.handles) child.visible = true;
      if (child.material) child.material.opacity = child.material.oldOpacity;
      if (child.parent == this.pickers || child.parent == this.hemiPicker || child.parent == this.subPickers) child.visible = false;
      if (child.parent == this.planes || child.parent == this.highlights) child.visible = false;
    });
    this.activePlane.visible = false;
  }

  highlight(axis) {
    this.traverse((child) => {
      if (child.material && child.material.highlight) {
        if (child.name == axis) {
          if (child.parent == this.highlights || child.parent == this.handles) child.visible = true;
          child.material.highlight(true);
        } else {
          child.material.highlight(false);
          child.material.opacity = 0.1;
        }
      }
    });
  }
}